import { SnapScroller } from '@almbrand/snapscroller';
import classNames from 'classnames';
import { PageTemplate } from 'components';
import { IsPackageComponentContext } from 'context/IsPackageComponentContext';
import { usePageParam } from 'hooks';
import { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { resetClearedState } from 'store/slices/clearSlice';
import { setModal } from 'store/slices/modalSlice';
import { addPage, incrementIndex, resetIndex, updateLast } from 'store/slices/pageListSlice';
import { isPageHidden } from '../../../services/DependencyService';
import { isPackageFields } from '../../../services/PackageService';
import pageTemplateStyle from '../PageTemplate/PageTemplate.module.scss';
import styles from './MultiplePageTemplate.module.scss';

interface ActiveContentArea {
	name: keyof ContentAreas;
	contentData: ContentData;
}

export interface MultiplePageTemplateProps {
	pageModel: PageModel;
}

export const MultiplePageTemplate: React.FC<MultiplePageTemplateProps> = memo(({ pageModel }) => {
	const [navigationIndex, setNavigationIndex] = useState(0);

	const pages = useAppSelector((state) => state.pages);
	const { contentArea, loadNewPage } = pages;

	const dispatch = useAppDispatch();

	const displayModal = useAppSelector((state) => state.modal.displayModal);
	const clearedState = useAppSelector((state) => state.clearedState);

	const { setPageParam, getPageParam } = usePageParam();
	const pageParam = getPageParam();

	const { setIsVisible, isVisible } = useContext(IsPackageComponentContext);

	const [skipHiddenPage, setSkipHiddenPage] = useState(0);

	const multiPageTemplateRef = useRef<HTMLDivElement>(null);
	const activePageRef = useRef<number>();
	const previousPageRef = useRef<number>();

	const activePages = useMemo(() => {
		const pages: ActivePage[] = [];
		Object.keys(contentArea).forEach((key: keyof ContentAreas) => {
			pages.push(...contentArea[key].content.activePages);
		});

		return pages;
	}, [contentArea]);

	const activePage = useMemo(() => {
		const currentPageId = pageParam ? Number.parseInt(pageParam, 10) : Number.NaN;
		if (Number.isNaN(currentPageId)) {
			return undefined;
		}

		return activePages.find((page) => page.contentId === currentPageId);
	}, [activePages, pageParam]);

	const activePageIndex = activePage?.navigationIndex;

	const isActivePagePackage = useMemo(() => {
		return isPackageFields(activePage?.fields);
	}, [activePage]);

	useEffect(() => {
		const wasReloaded = sessionStorage.getItem('reloaded');
		const hasActivePages = pages.numberOfActivePages > 0;

		// Clean up reload flag
		if (wasReloaded) {
			sessionStorage.removeItem('reloaded');
		}

		// Reset cleared state after page reload
		if (wasReloaded && clearedState.isCleared) {
			dispatch(resetClearedState());
		}

		// Set reload flag if state was cleared (user returned after finishing)
		if (clearedState.isCleared) {
			sessionStorage.setItem('reloaded', 'true');
		}

		// Exit early if pages already loaded (prevents re-initialization)
		if ((clearedState.isCleared || wasReloaded) && hasActivePages) {
			return;
		}

		// Get the active content area
		const activeContentArea = getActiveContentArea();
		if (!activeContentArea) return; // Early return if no active content area

		const { name, contentData } = activeContentArea;
		const { sectionIndex, pageIndex } = contentData ?? {};

		const page = pageModel[name]?.items[sectionIndex]?.content.steps.items[pageIndex]?.content;
		const sectionLength = pageModel[name]?.items.length;
		const pageLength = pageModel[name]?.items[sectionIndex]?.content.steps.items.length;

		if (page) {
			// initial hidden by dependency rule
			const isHiddenInitially = isPageHidden(page);

			if (isHiddenInitially && skipHiddenPage !== page.contentId) {
				// causes this effect to run again
				setSkipHiddenPage(page.contentId);
			}

			// add index, section and area to page
			page.pageIndex = pageIndex;
			page.sectionIndex = sectionIndex;
			page.area = name;

			dispatch(
				addPage({
					contentArea: name,
					page: page,
				})
			);
			if (!isHiddenInitially) {
				setPageParam(page.contentId.toString().replace('/', ''));
			}

			//Check if last page/step
			if (pageIndex === pageLength - 1) {
				dispatch(incrementIndex({ contentArea: name, whatIndex: 'section' }));
				dispatch(resetIndex({ contentArea: name, whatIndex: 'page' }));
				//Check if it's the last section in contentArea
				if (sectionIndex === sectionLength - 1) {
					dispatch(updateLast({ contentArea: name, lastPage: true }));
					dispatch(updateLast({ contentArea: name, lastSection: true }));
				}
			}
		}
	}, [loadNewPage, skipHiddenPage]);

	//update SnapScroller index
	useEffect(() => {
		const activePageParam = pageParam ? Number.parseInt(pageParam, 10) : Number.NaN;

		if (!Number.isNaN(activePageParam)) {
			if (activePageRef.current !== activePageParam) {
				previousPageRef.current = activePageRef.current;
			}
			activePageRef.current = activePageParam;
		}

		if (activePageIndex !== undefined && activePageIndex !== -1) {
			setNavigationIndex(activePageIndex);
		}
		// when page changes always close open modals
		if (displayModal) {
			dispatch(setModal({ displayModal: false }));
		}
	}, [activePageIndex, dispatch, pageParam]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setIsVisible(isActivePagePackage);
		}, 100);

		return () => clearTimeout(timer);
	}, [isActivePagePackage, setIsVisible]);

	// Scroll to top when on next page when zoomed in on desktop
	useEffect(() => {
		if (multiPageTemplateRef.current) {
			const child = multiPageTemplateRef.current.firstChild as HTMLElement;
			child.scrollTop > 0 && child.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}, [pageParam]);
	// ----------------------FOR TESTING PURPOSES---------------------------------//
	// This is a test for get the correct value of scroll delay
	// ---------------------------------------------------------------------------//
	const [searchParams] = useSearchParams();
	const getDelayParam = () => searchParams.get('scrollDelay');
	// ---------------------------------------------------------------------------//

	const getActiveContentArea = (): ActiveContentArea | undefined => {
		for (const key of Object.keys(contentArea).reduce<Array<keyof ContentAreas>>((result, currentKey) => {
			result.push(currentKey as keyof ContentAreas);
			return result;
		}, [])) {
			const { pageLast, sectionLast, sectionIndex } = contentArea[key].content;
			if (sectionLast && pageLast) {
				continue;
			}

			const items = pageModel[key]?.items;
			if (items?.length && items[sectionIndex]?.content.steps.items.length) {
				return {
					name: key,
					contentData: contentArea[key].content,
				};
			}
		}

		return undefined;
	};

	const mapActivePages = () => {
		return activePages.map((page) => (
			<PageTemplate
				key={page.contentId}
				stepBlock={page}
				activePage={activePageIndex}
			/>
		));
	};

	const collectActivePages = () => {
		return activePages;
	};

	return (
		<div
			className={classNames(styles.MultiplePageTemplate, isVisible && styles['MultiplePageTemplate--background'])}
			ref={multiPageTemplateRef}
		>
			<SnapScroller
				activeElement={navigationIndex}
				targetClass={pageTemplateStyle.PageTemplate}
				delay={Number.parseInt(getDelayParam(), 10) || Infinity}
				onIndexChange={(index) => {
					const activePages = collectActivePages();
					setPageParam(activePages[index].contentId.toString().replace('/', ''));
				}}
				className={styles.MultiplePageTemplate__SnapScroller}
				childClassName={!isVisible && classNames(styles.MultiplePageTemplate__SnapScroller_child)}
			>
				{mapActivePages()}
			</SnapScroller>
		</div>
	);
});

MultiplePageTemplate.displayName = 'MultiplePageTemplate';
